import React from 'react';
import { Phone, MessageSquare, UserCheck, ArrowRight } from 'lucide-react';

const HowItWorks = () => {
  const steps = [
    {
      number: '01',
      icon: Phone,
      title: 'Contact Us',
      description: 'Reach out using our contact form or phone. We\'re here to listen and understand your needs.',
      color: 'from-cyan-500 to-cyan-600'
    },
    {
      number: '02',
      icon: MessageSquare,
      title: 'Discuss Your Needs',
      description: 'We talk through the type of support required, schedule preferences, and any specific requirements.',
      color: 'from-teal-500 to-teal-600'
    },
    {
      number: '03',
      icon: UserCheck,
      title: 'Start Receiving Support',
      description: 'Personalised home support visits begin at times that suit you, with regular communication to ensure everything is going well.',
      color: 'from-emerald-500 to-emerald-600'
    }
  ];

  return (
    <section id="how-it-works" className="py-20 bg-gradient-to-b from-white to-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">How It Works</span>
          <h2 className="text-4xl font-bold text-slate-900 mt-2 mb-4">
            Getting Started Is Simple
          </h2>
          <p className="text-lg text-slate-600">
            We've made it easy to begin receiving the support you or your loved one needs in just three simple steps.
          </p>
        </div>

        {/* Steps */}
        <div className="relative">
          {/* Connection Line (Desktop) */}
          <div className="hidden lg:block absolute top-32 left-0 right-0 h-1 bg-gradient-to-r from-cyan-200 via-teal-200 to-emerald-200" style={{ width: '80%', margin: '0 10%' }}></div>

          <div className="grid lg:grid-cols-3 gap-8 lg:gap-12 relative">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="relative">
                  {/* Step Card */}
                  <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-slate-100 hover:border-teal-200 transition-all duration-300 hover:shadow-xl group">
                    {/* Step Number */}
                    <div className="absolute -top-6 -left-6 w-16 h-16 bg-gradient-to-br from-slate-100 to-slate-200 rounded-full flex items-center justify-center shadow-lg">
                      <span className="text-2xl font-bold text-slate-700">{step.number}</span>
                    </div>

                    {/* Icon */}
                    <div className={`w-20 h-20 bg-gradient-to-br ${step.color} rounded-2xl flex items-center justify-center mb-6 mt-4 group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className="w-10 h-10 text-white" />
                    </div>

                    {/* Content */}
                    <h3 className="text-2xl font-bold text-slate-900 mb-4">
                      {step.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed text-lg">
                      {step.description}
                    </p>
                  </div>

                  {/* Arrow (Desktop) */}
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-32 -right-6 z-10">
                      <ArrowRight className="w-8 h-8 text-teal-400" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <p className="text-xl text-slate-700 mb-6">
            Ready to get started? We're here to help.
          </p>
          <a 
            href="#contact"
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-teal-600 to-cyan-600 text-white font-semibold rounded-xl hover:from-teal-700 hover:to-cyan-700 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            Contact Us Today
            <ArrowRight className="ml-2 w-5 h-5" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
